import { memo, useCallback, useEffect, useRef } from "react";
import { Keyboard, Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import Animated, {
  FadeIn,
  FadeOut,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { FormInput, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { TabsHeader } from "@src/components/TabsHeader";
import { Text } from "@src/components/Text";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { CameraSearchSheet } from "./CameraSearchSheet.native";
import { CamerasTabHeaderActions } from "./CamerasTabHeaderActions.native";
import { styles } from "./CamerasTabHeader.native";
import type { CameraSearchSheetHandle } from "./CameraSearchSheet.native";
import type { FC } from "react";
import type { SiteWideCamerasHeaderProps } from "./CamerasTabHeader.native";

const AlarmFilterPill: FC<{ label: string; isActive: boolean; onPress: () => void }> = memo(
  ({ label, isActive, onPress }) => {
    const { theme } = useUnistyles();
    const progress = useSharedValue(isActive ? 1 : 0);

    useEffect(() => {
      progress.value = withTiming(isActive ? 1 : 0, { duration: 250 });
    }, [isActive, progress]);

    const animatedStyle = useAnimatedStyle(() => ({
      backgroundColor: interpolateColor(
        progress.value,
        [0, 1],
        ["transparent", theme.colors.interactive.buttons.default_background as string],
      ),
    }));

    return (
      <Pressable onPress={onPress}>
        <Animated.View style={[styles.labelFilter, animatedStyle]}>
          <Text
            textStyle="sm/regular"
            style={{ color: isActive ? theme.colors.white.default : theme.colors.textMuted }}
          >
            {label}
          </Text>
        </Animated.View>
      </Pressable>
    );
  },
);

AlarmFilterPill.displayName = "AlarmFilterPill";

export const CamerasTabSiteWideHeader: FC<SiteWideCamerasHeaderProps> = memo(
  ({ selectedSiteName, onHamburgerMenuPress, onClearSite, alarmFilter, onAlarmFilterToggle, ...sharedProps }) => {
    const { theme } = useUnistyles();
    const isCrossSearchEnabled = useBooleanFlag(FEATURE_FLAGS.CAMERA_SEARCH);
    const searchSheetRef = useRef<CameraSearchSheetHandle>(null);

    const handleSearchPress = useCallback(() => {
      if (isCrossSearchEnabled) {
        searchSheetRef.current?.present();
      } else {
        sharedProps.onSearchPress();
      }
    }, [isCrossSearchEnabled, sharedProps]);

    return (
      <>
        <TabsHeader
          selectedSiteName={selectedSiteName}
          onHamburgerMenuPress={onHamburgerMenuPress}
          onClearSite={onClearSite}
        />
        <View style={styles.toolbar}>
          <View style={[styles.toolbarRow, styles.siteWideToolbarRow]}>
            <View flexDirection="row" alignItems="center" gap="md" flex={1}>
              <AlarmFilterPill
                label="Armed"
                isActive={alarmFilter === "armed"}
                onPress={() => onAlarmFilterToggle("armed")}
              />
              <AlarmFilterPill
                label="Disarmed"
                isActive={alarmFilter === "disarmed"}
                onPress={() => onAlarmFilterToggle("disarmed")}
              />
            </View>
            <CamerasTabHeaderActions
              onSearchPress={handleSearchPress}
              variant="siteWide"
              hasAlert={isCrossSearchEnabled && !!sharedProps.searchQuery}
            />
          </View>

          {!isCrossSearchEnabled && sharedProps.searchExpanded && (
            <Animated.View style={styles.searchBarWrap} entering={FadeIn} exiting={FadeOut}>
              <FormInput
                ref={sharedProps.searchInputRef}
                placeholder="Search cameras"
                value={sharedProps.searchQuery}
                onChangeText={sharedProps.onSearchQueryChange}
                size="small"
                returnKeyType="search"
                onSubmitEditing={() => Keyboard.dismiss()}
                style={styles.searchInput}
                iconLeft={<Icon name="search" size={20} color={theme.colors.textMuted} />}
                iconRight={
                  sharedProps.searchQuery ?
                    <Pressable
                      onPress={() => sharedProps.onSearchQueryChange("")}
                      accessibilityRole="button"
                      accessibilityLabel="Clear search"
                    >
                      <Icon name="closeCircle" size={20} color={theme.colors.textMuted} />
                    </Pressable>
                  : undefined
                }
              />
            </Animated.View>
          )}
        </View>

        {isCrossSearchEnabled && (
          <CameraSearchSheet
            ref={searchSheetRef}
            searchQuery={sharedProps.searchQuery}
            onSearchQueryChange={sharedProps.onSearchQueryChange}
          />
        )}
      </>
    );
  },
);

CamerasTabSiteWideHeader.displayName = "CamerasTabSiteWideHeader";
